import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Info } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { describe as describeProduct } from "./productCopy";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { useShareGate } from "@/hooks/useShareGate";
import { NoticeBox } from "@/components/NoticeBox";

function splitLines(text: string): string[] {
  return text
    .split("\n")
    .map(l => l.trim())
    .filter(Boolean);
}

const TOOL_INFO_CONTENT = {
  what: `Assembles product features, benefits, and audience details into a few draft description structures using proven copywriting formulas, giving you a solid starting point to edit rather than a blank page.`,
  how: `Enter your product name, features, benefits, and target audience, and three different draft structures are generated automatically — Feature-to-Benefit, Problem-to-Solution, and Short & Punchy.`,
  tip: `Treat the output as a first draft to personalize, not a finished listing — the specific details that make your product distinct from similar ones are worth adding in by hand.`,
};

export default function ProductDescriptionWriter() {
  const [name, setName] = useState("AquaFlow Water Bottle");
  const [category, setCategory] = useState("insulated water bottle");
  const [features, setFeatures] = useState(
    "24-hour cold retention\nLeak-proof lid\nBPA-free stainless steel\nFits standard cup holders"
  );
  const [benefits, setBenefits] = useState(
    "Stays cold all day at work or the gym\nNo more soggy bags from leaks\nSafe for daily use"
  );
  const [audience, setAudience] = useState("busy professionals and gym-goers");

  const featureList = useMemo(() => splitLines(features), [features]);
  const benefitList = useMemo(() => splitLines(benefits), [benefits]);
  const variants = useMemo(
    () => describeProduct({ name, category, audience, features: featureList, benefits: benefitList }),
    [name, category, audience, featureList, benefitList]
  );

  const copy = (text: string) => gateShare(async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); return true; }
    catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; }
  });

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });
  const { gateShare } = useShareGate();


  return (
    <ToolContainer>
      <NoticeBox className="p-4 mb-6">
        <div className="flex gap-2">
          <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm">
            This assembles your product details into a few proven copywriting
            structures — it's a solid, honest starting point, not AI-generated
            creative writing. For fully polished, on-brand copy, an AI writing
            tool will do better; use these as a draft to edit from.
          </p>
        </div>
      </NoticeBox>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="mb-1 block text-sm">Product Name</Label>
          <Input aria-label="Product Name" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div>
          <Label className="mb-1 block text-sm">Category</Label>
          <Input aria-label="Category"
            value={category}
            onChange={e => setCategory(e.target.value)}
            placeholder="e.g. wireless headphones"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="mb-1 block text-sm">
            Key Features (one per line)
          </Label>
          <Textarea
            value={features}
            onChange={e => setFeatures(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="mb-1 block text-sm">
            Key Benefits (one per line)
          </Label>
          <Textarea
            value={benefits}
            onChange={e => setBenefits(e.target.value)}
            className="min-h-[80px]"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="mb-1 block text-sm">Target Audience</Label>
          <Input aria-label="Target Audience" value={audience} onChange={e => setAudience(e.target.value)} />
        </div>
      </div>

      <div className="space-y-4">
        {blocked && <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>}
        {!blocked && variants.map(v => (
          <Card key={v.label} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {v.label}
              </p>
              <Button size="sm" variant="outline" onClick={() => copy(v.text)}>
                Copy
              </Button>
            </div>
            <p className="text-sm leading-relaxed">{v.text}</p>
          </Card>
        ))}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
